import React from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * ReviewerLockdown — mounts a sticky "View-only" banner + a global
 * CSS lockdown on interactive controls when the current user's role
 * is `reviewer` (Apple App Store reviewer accounts, or any future
 * view-only admin).
 *
 * Contract:
 *   • Render <ReviewerLockdown /> once at the top of any admin-scope
 *     page. It becomes a no-op for `admin` (and everyone else).
 *   • For reviewers, injects a `<style>` block that disables every
 *     button and every mutating input on the page. Elements can opt
 *     out via `data-reviewer-safe="true"` — used for nav links,
 *     collapse toggles, and other read-only interactions.
 *
 * Defense in depth: the backend already 403s reviewer writes via the
 * `current_admin_write` guard (`deps.py::current_admin`). The banner
 * exists to make the state legible; the CSS lockdown just prevents
 * confusing "why is this greyed out" toasts on accidental clicks.
 */
export default function ReviewerLockdown() {
  const { user } = useAuth();
  const isReviewer = user?.role === "reviewer";
  if (!isReviewer) return null;
  return (
    <>
      <style>{`
        .reviewer-locked button:not([data-reviewer-safe]):not(.reviewer-safe),
        .reviewer-locked input[type="file"]:not([data-reviewer-safe]),
        .reviewer-locked textarea:not([data-reviewer-safe]),
        .reviewer-locked input:not([type="search"]):not([type="button"]):not([data-reviewer-safe]),
        .reviewer-locked select:not([data-reviewer-safe]),
        .reviewer-locked label[role="button"]:not([data-reviewer-safe]) {
          pointer-events: none !important;
          opacity: 0.55 !important;
          cursor: not-allowed !important;
          filter: grayscale(20%);
        }
        .reviewer-locked form:not([data-reviewer-safe]) {
          position: relative;
        }
        .reviewer-locked [data-reviewer-safe],
        .reviewer-locked [data-reviewer-safe] * {
          pointer-events: auto !important;
          opacity: 1 !important;
          cursor: auto !important;
          filter: none !important;
        }
      `}</style>
      <div
        data-testid="reviewer-view-only-banner"
        className="sticky top-0 z-50 bg-tb-navy text-tb-gold-bright font-hand text-sm px-4 py-2 flex items-center justify-center gap-2 border-b-2 border-tb-gold shadow"
        role="status"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="11" width="18" height="10" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span>
          Admin View — Apple Review Access · Modifications disabled
        </span>
      </div>
    </>
  );
}

/**
 * Convenience hook — returns `true` when the current user is a
 * reviewer. Use to short-circuit imperative write flows (e.g. before
 * calling `api.post(...)`) so the user sees an inline toast instead
 * of a network 403.
 */
export function useIsReviewer() {
  const { user } = useAuth();
  return user?.role === "reviewer";
}

/**
 * Full-admin check — used to gate admin-only UI affordances like the
 * "delete any lounge post" trash button. Distinct from `useIsReviewer`
 * because reviewers must NOT see write-only affordances.
 */
export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === "admin";
}
